/**
 * Email Template Generator - Premium Edition
 *
 * Generates stunning, Mailchimp-worthy HTML email templates for HMC events.
 * Uses table-based layout with inline CSS for maximum email client compatibility.
 *
 * Premium Features:
 * - Hero section with gradient overlay
 * - Gradient border event cards with VML fallbacks for Outlook
 * - YouTube thumbnails with play button overlay
 * - Artist descriptions for concerts
 * - Team-specific color themes
 * - Guest link CTA button
 * - Mobile-responsive design
 *
 * Email Client Compatibility:
 * - Outlook Desktop (VML fallbacks for gradients/borders)
 * - Outlook Mobile
 * - Gmail (Web + Mobile)
 * - Apple Mail (Mac + iOS)
 * - Yahoo Mail
 */

import type { HMCEvent, MusicGenre } from '../hmcEventsStore';
import { parseLocalDate } from './dateUtils';

export interface EmailTemplateConfig {
  events: HMCEvent[];
  personalMessage: string;
  senderName: string;
  includeFooter: boolean;
  includeLogo: boolean;
  // Premium options
  includeGuestLink?: boolean;
  guestLinkUrl?: string;
  guestLinkExpiration?: number;
  stylePreset?: 'premium' | 'classic' | 'minimal';
  showArtistDescriptions?: boolean;
  showYouTubeThumbnails?: boolean;
  // Category groupings for section headers
  eventCategories?: {
    sporting: HMCEvent[];
    concerts: HMCEvent[];
    other: HMCEvent[];
  };
  // Email signature HTML (preserves Outlook formatting)
  signatureHtml?: string;
}

// USI Corporate Branding
const USI_BRANDING = {
  logo: 'https://www.usi.com/siteassets/images/usi_logo_web-2.png',
  logoWhite: 'https://www.usi.com/siteassets/images/usi_logo_white_web.png',
  navy: '#1a365d',
  navyDark: '#0f2744',
  navyLight: '#2c5282',
  gold: '#d69e2e',
  goldLight: '#ecc94b',
};

// Team gradient themes with VML fallback colors for Outlook
const TEAM_GRADIENTS = {
  dynamo: {
    primary: '#f97316',
    secondary: '#ea580c',
    gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
    text: 'DYNAMO',
    accent: '#fed7aa',
  },
  dash: {
    primary: '#0ea5e9',
    secondary: '#0284c7',
    gradient: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    text: 'DASH',
    accent: '#bae6fd',
  },
  rockets: {
    primary: '#dc2626',
    secondary: '#b91c1c',
    gradient: 'linear-gradient(135deg, #dc2626, #b91c1c)',
    text: 'ROCKETS',
    accent: '#fecaca',
  },
  concert: {
    primary: '#8b5cf6',
    secondary: '#7c3aed',
    gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    text: 'CONCERT',
    accent: '#ddd6fe',
  },
  general: {
    primary: '#4f46e5',
    secondary: '#4338ca',
    gradient: 'linear-gradient(135deg, #4f46e5, #4338ca)',
    text: 'EVENT',
    accent: '#c7d2fe',
  },
};

// Genre display labels and colors for email
const GENRE_COLORS: Record<MusicGenre, { bg: string; label: string }> = {
  rap: { bg: '#d97706', label: 'Hip-Hop' },
  rnb: { bg: '#9333ea', label: 'R&B' },
  latino: { bg: '#dc2626', label: 'Latino' },
  rock: { bg: '#475569', label: 'Rock' },
  pop: { bg: '#db2777', label: 'Pop' },
  country: { bg: '#ca8a04', label: 'Country' },
  electronic: { bg: '#0891b2', label: 'EDM' },
  other: { bg: '#6b7280', label: 'Other' },
};

/**
 * Extract YouTube video ID from various URL formats
 */
export function extractYouTubeVideoId(url: string | null): string | null {
  if (!url) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Get the best available image URL for an event
 * Priority: event_image_url > YouTube thumbnail > sport placeholder
 */
export function getEventImageUrl(event: HMCEvent): string | null {
  // Priority 1: Direct event image URL (from scraping)
  if (event.event_image_url) {
    return event.event_image_url;
  }

  // Priority 2: YouTube thumbnail (use i.ytimg.com CDN for better reliability)
  if (event.youtube_url) {
    const videoId = extractYouTubeVideoId(event.youtube_url);
    if (videoId) {
      return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
    }
  }

  // Priority 3: No image available - return null (will use placeholder)
  return null;
}

/**
 * Generate inline SVG placeholder for sports events
 */
function generateSportsPlaceholder(eventType: 'dynamo' | 'dash' | 'general'): string {
  const colors = {
    dynamo: { primary: '#f97316', secondary: '#ea580c', text: 'DYNAMO' },
    dash: { primary: '#0ea5e9', secondary: '#0284c7', text: 'DASH' },
    general: { primary: '#8b5cf6', secondary: '#7c3aed', text: 'EVENT' },
  };

  const { primary, secondary, text } = colors[eventType] || colors.general;

  return `
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
      <tr>
        <td style="background: linear-gradient(135deg, ${primary} 0%, ${secondary} 100%); height: 160px; text-align: center; vertical-align: middle;">
          <span style="font-family: Arial, sans-serif; font-size: 32px; font-weight: bold; color: white; letter-spacing: 2px; text-shadow: 0 2px 4px rgba(0,0,0,0.2);">
            ${text}
          </span>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char] || char);
}

/**
 * Format date for email display
 * Uses parseLocalDate to avoid timezone shift bugs
 */
function formatEventDate(dateStr: string): string {
  const date = parseLocalDate(dateStr);
  if (!date) return 'TBD';
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Clean event name (remove attendance info)
 */
function getCleanEventName(opponent: string | null): string {
  if (!opponent) return 'Event';

  // Remove attendance patterns like "Name (Company) - 4 tickets"
  const cleaned = opponent
    .replace(/\s*[-–]\s*\d+\s*(tickets?|tix|seats?).*$/i, '')
    .replace(/\s*\(\d+\s*(tickets?|tix|seats?)\).*$/i, '')
    .trim();

  return cleaned || 'Event';
}

/**
 * Check if event is a sporting event (Dynamo, Dash, Rockets, Astros, Texans)
 */
function isSportingEvent(event: HMCEvent): boolean {
  const eventType = event.event_type || 'general';
  const opponent = event.opponent?.toLowerCase() || '';

  return (
    eventType === 'dynamo' ||
    eventType === 'dash' ||
    opponent.includes('rockets') ||
    opponent.includes('astros') ||
    opponent.includes('texans')
  );
}

/**
 * Get venue for event - Dynamo/Dash always play at Shell Stadium
 */
function getEventVenue(event: HMCEvent): string {
  const eventType = event.event_type || 'general';
  const isDynamoOrDash = eventType === 'dynamo' || eventType === 'dash';
  return isDynamoOrDash ? 'Shell Stadium' : (event.venue || 'Toyota Center');
}

/**
 * Get team theme from event type
 */
function getTeamTheme(event: HMCEvent): (typeof TEAM_GRADIENTS)['dynamo'] {
  const eventType = event.event_type || 'general';

  // Check if it's a concert (has music genre)
  if (event.music_genre && event.music_genre !== 'other') {
    return TEAM_GRADIENTS.concert;
  }

  // Check for Rockets
  if (
    event.opponent?.toLowerCase().includes('rockets') ||
    (event.venue?.toLowerCase().includes('toyota center') &&
      eventType !== 'dynamo' &&
      eventType !== 'dash')
  ) {
    return TEAM_GRADIENTS.rockets;
  }

  if (eventType === 'dynamo') return TEAM_GRADIENTS.dynamo;
  if (eventType === 'dash') return TEAM_GRADIENTS.dash;

  return TEAM_GRADIENTS.general;
}

/**
 * Generate YouTube thumbnail with play button overlay
 */
function generateYouTubeThumbnail(videoId: string, eventName: string): string {
  const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;

  return `
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="margin: 16px 0;">
      <tr>
        <td>
          <a href="${youtubeUrl}" target="_blank" style="text-decoration: none; display: block;">
            <!-- Thumbnail Container - Full Width -->
            <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
              <tr>
                <td style="position: relative; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
                  <!-- Thumbnail Image - Full Width -->
                  <img
                    src="${thumbnailUrl}"
                    alt="Watch ${escapeHtml(eventName)} on YouTube"
                    width="600"
                    style="display: block; width: 100%; height: auto; border-radius: 12px;"
                  />
                  <!-- Play Button Overlay - Centered using table -->
                  <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0;">
                    <tr>
                      <td align="center" valign="middle" style="height: 100%;">
                        <table cellpadding="0" cellspacing="0" role="presentation">
                          <tr>
                            <td align="center" valign="middle" style="background: rgba(220, 38, 38, 0.95); width: 72px; height: 52px; border-radius: 14px; box-shadow: 0 4px 20px rgba(0,0,0,0.4);">
                              <span style="color: #ffffff; font-size: 26px; margin-left: 4px;">&#9654;</span>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </a>
          <p style="margin: 10px 0 0; font-family: Arial, sans-serif; font-size: 13px; color: #6b7280; text-align: center;">
            <a href="${youtubeUrl}" target="_blank" style="color: #dc2626; text-decoration: none; font-weight: 600;">
              &#9654; Watch on YouTube
            </a>
          </p>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Professional icon URLs from Icons8 CDN
 * Using filled style for better visibility in emails
 * Color is embedded in URL (hex without #)
 */
const ICON_URLS = {
  // Gray icons for standard text (374151)
  calendar: 'https://img.icons8.com/ios-filled/20/374151/calendar--v1.png',
  clock: 'https://img.icons8.com/ios-filled/20/374151/clock--v1.png',
  mapPin: 'https://img.icons8.com/ios-filled/20/374151/marker--v1.png',
  // Colored icons for themed sections
  calendarColored: (color: string) => `https://img.icons8.com/ios-filled/20/${color.replace('#', '')}/calendar--v1.png`,
  clockColored: (color: string) => `https://img.icons8.com/ios-filled/20/${color.replace('#', '')}/clock--v1.png`,
  mapPinColored: (color: string) => `https://img.icons8.com/ios-filled/20/${color.replace('#', '')}/marker--v1.png`,
};

/**
 * Generate inline icon HTML for email
 */
function emailIcon(type: 'calendar' | 'clock' | 'mapPin', color?: string): string {
  const url = color
    ? ICON_URLS[`${type}Colored`](color)
    : ICON_URLS[type];
  return `<img src="${url}" alt="" width="16" height="16" style="vertical-align:middle;margin-right:8px;display:inline-block;" />`;
}

/**
 * Generate compact event card - optimized for Gmail (prevents clipping)
 * Used when 3+ events are selected to keep email under 102KB
 * Removes: VML fallbacks, HTML comments, excessive styling, YouTube embeds
 * Note: YouTube links are NEVER shown for sporting events
 */
function generateCompactEventCard(event: HMCEvent): string {
  const imageUrl = getEventImageUrl(event);
  const eventName = getCleanEventName(event.opponent);
  const formattedDate = formatEventDate(event.event_date);
  const time = event.kickoff_time || event.event_start_time || 'TBD';
  const venue = getEventVenue(event);
  const theme = getTeamTheme(event);
  const isSporting = isSportingEvent(event);

  // Genre badge for concerts (simplified)
  let genreBadge = '';
  if (event.music_genre && event.music_genre !== 'other') {
    const genreInfo = GENRE_COLORS[event.music_genre];
    genreBadge = `<span style="display:inline-block;background:${genreInfo.bg};color:#fff;padding:4px 12px;border-radius:12px;font-size:11px;font-weight:700;margin-bottom:8px;">${genreInfo.label}</span>`;
  }

  // Compact image or colored header
  const headerSection = imageUrl
    ? `<tr><td><img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(eventName)}" width="536" style="display:block;width:100%;height:auto;max-height:180px;object-fit:cover;border-radius:8px 8px 0 0;"/></td></tr>`
    : `<tr><td style="background:${theme.primary};padding:24px;text-align:center;border-radius:8px 8px 0 0;"><h3 style="margin:0;font-family:Arial,sans-serif;font-size:20px;font-weight:bold;color:#fff;">${escapeHtml(eventName)}</h3></td></tr>`;

  // YouTube link (text only, no embed) - NEVER for sporting events
  const youtubeLink = event.youtube_url && !isSporting
    ? `<p style="margin:12px 0 0;"><a href="${escapeHtml(event.youtube_url)}" target="_blank" style="color:#dc2626;font-size:13px;text-decoration:none;">&#9654; Watch on YouTube</a></p>`
    : '';

  // Add visual separator before card for clearer distinction
  return `
<table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:8px;">
<tr><td style="padding:0 16px;"><div style="height:1px;background:linear-gradient(90deg,transparent,${theme.primary}50,transparent);"></div></td></tr>
</table>
<table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;border:2px solid ${theme.primary}60;border-radius:12px;overflow:hidden;box-shadow:0 4px 12px rgba(0,0,0,0.08);">
${headerSection}
<tr><td style="padding:20px;background:#fff;">
${genreBadge}
${imageUrl ? `<h3 style="margin:0 0 12px;font-family:Arial,sans-serif;font-size:18px;font-weight:bold;color:#1f2937;">${escapeHtml(eventName)}</h3>` : ''}
<p style="margin:0 0 8px;font-family:Arial,sans-serif;font-size:14px;color:#374151;">${emailIcon('calendar')}${escapeHtml(formattedDate)}</p>
<p style="margin:0 0 8px;font-family:Arial,sans-serif;font-size:14px;color:#374151;">${emailIcon('clock')}${escapeHtml(time)}</p>
<p style="margin:0;font-family:Arial,sans-serif;font-size:14px;color:#374151;">${emailIcon('mapPin')}${escapeHtml(venue)}</p>
${youtubeLink}
</td></tr>
</table>`;
}

/**
 * Generate unified event card - hero style with full details
 * Used for all events (both first and second)
 */
function generateUnifiedEventCard(
  event: HMCEvent,
  config: { showYouTube?: boolean; showDescription?: boolean; compact?: boolean }
): string {
  // Use compact mode for smaller email size (prevents Gmail clipping)
  if (config.compact) {
    return generateCompactEventCard(event);
  }

  const imageUrl = getEventImageUrl(event);
  const eventName = getCleanEventName(event.opponent);
  const formattedDate = formatEventDate(event.event_date);
  const time = event.kickoff_time || event.event_start_time || 'TBD';
  const venue = getEventVenue(event);
  const theme = getTeamTheme(event);
  const videoId = event.youtube_url ? extractYouTubeVideoId(event.youtube_url) : null;
  const isSporting = isSportingEvent(event);

  // Genre badge for concerts
  let genreBadge = '';
  if (event.music_genre && event.music_genre !== 'other') {
    const genreInfo = GENRE_COLORS[event.music_genre];
    genreBadge = `
      <span style="display: inline-block; background: linear-gradient(135deg, ${genreInfo.bg}, ${genreInfo.bg}dd); color: white; padding: 6px 16px; border-radius: 20px; font-family: Arial, sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 0.5px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); margin-right: 8px;">
        ${genreInfo.label}
      </span>
    `;
  }

  // YouTube section - NEVER show for sporting events
  let youtubeSection = '';
  if (config.showYouTube !== false && videoId && !isSporting) {
    youtubeSection = generateYouTubeThumbnail(videoId, eventName);
  }

  // Description section
  let descriptionSection = '';
  if (config.showDescription !== false) {
    descriptionSection = generateDescriptionSection(event);
  }

  // Hero image or gradient placeholder
  const heroImageSection = imageUrl
    ? `
      <tr>
        <td style="position: relative;">
          <img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(eventName)}" width="600" style="display: block; width: 100%; height: auto; min-height: 200px; max-height: 320px; object-fit: cover;"/>
          <!--[if !mso]><!-->
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="position: absolute; bottom: 0; left: 0; right: 0;">
            <tr>
              <td style="background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 60%, transparent 100%); padding: 60px 28px 24px;">
                ${genreBadge}
                <h2 style="margin: ${genreBadge ? '12px' : '0'} 0 0 0; font-family: Arial, sans-serif; font-size: 26px; font-weight: bold; color: #ffffff; text-shadow: 0 2px 8px rgba(0,0,0,0.5); line-height: 1.2;">
                  ${escapeHtml(eventName)}
                </h2>
              </td>
            </tr>
          </table>
          <!--<![endif]-->
        </td>
      </tr>
      <!--[if mso]>
      <tr><td style="background-color: ${theme.primary}; padding: 20px 28px;"><h2 style="margin: 0; font-family: Arial, sans-serif; font-size: 22px; font-weight: bold; color: #ffffff;">${escapeHtml(eventName)}</h2></td></tr>
      <![endif]-->
    `
    : `
      <tr>
        <td>
          <!--[if mso]>
          <v:rect xmlns:v="urn:schemas-microsoft-com:vml" style="width:600px;height:180px;" fillcolor="${theme.primary}">
            <v:textbox style="mso-fit-shape-to-text:false;" inset="28px,50px,28px,28px"><center><span style="font-family:Arial,sans-serif;font-size:26px;font-weight:bold;color:#ffffff;">${escapeHtml(eventName)}</span></center></v:textbox>
          </v:rect>
          <![endif]-->
          <!--[if !mso]><!-->
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background: ${theme.gradient};">
            <tr>
              <td style="padding: 50px 28px 40px; text-align: center;">
                ${genreBadge ? `<div style="margin-bottom: 12px;">${genreBadge}</div>` : ''}
                <h2 style="margin: 0; font-family: Arial, sans-serif; font-size: 28px; font-weight: bold; color: #ffffff; letter-spacing: 0.5px; text-shadow: 0 2px 4px rgba(0,0,0,0.2);">
                  ${escapeHtml(eventName)}
                </h2>
              </td>
            </tr>
          </table>
          <!--<![endif]-->
        </td>
      </tr>
    `;

  return `
    <!-- Event Separator - Clear visual break between events -->
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="margin-bottom: 12px;">
      <tr>
        <td style="padding: 0 20px;">
          <div style="height: 2px; background: linear-gradient(90deg, transparent, ${theme.primary}60, transparent);"></div>
        </td>
      </tr>
    </table>
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation" class="event-card" style="margin-bottom: 32px; border-radius: 16px; overflow: hidden; box-shadow: 0 6px 24px rgba(0,0,0,0.12); border: 2px solid ${theme.primary}40;">
      ${heroImageSection}
      <tr>
        <td style="background-color: #ffffff; padding: 24px 28px;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background: #f8fafc; border-radius: 12px; padding: 20px; border: 1px solid #e2e8f0;">
            <tr>
              <td>
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                  <tr>
                    <td width="32" valign="top" style="padding-right: 12px; padding-bottom: 14px;">
                      <div style="width: 32px; height: 32px; background: ${theme.primary}15; border-radius: 8px; text-align: center; line-height: 32px;">
                        <img src="https://img.icons8.com/ios-filled/24/${theme.primary.replace('#', '')}/calendar.png" alt="" width="18" height="18" style="vertical-align: middle;" />
                      </div>
                    </td>
                    <td style="padding-bottom: 14px;">
                      <p style="margin: 0; font-family: Arial, sans-serif; font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Date</p>
                      <p style="margin: 4px 0 0 0; font-family: Arial, sans-serif; font-size: 15px; color: #1e293b; font-weight: 600;">${escapeHtml(formattedDate)}</p>
                    </td>
                  </tr>
                  <tr>
                    <td width="32" valign="top" style="padding-right: 12px; padding-bottom: 14px;">
                      <div style="width: 32px; height: 32px; background: ${theme.primary}15; border-radius: 8px; text-align: center; line-height: 32px;">
                        <img src="https://img.icons8.com/ios-filled/24/${theme.primary.replace('#', '')}/clock.png" alt="" width="18" height="18" style="vertical-align: middle;" />
                      </div>
                    </td>
                    <td style="padding-bottom: 14px;">
                      <p style="margin: 0; font-family: Arial, sans-serif; font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Time</p>
                      <p style="margin: 4px 0 0 0; font-family: Arial, sans-serif; font-size: 15px; color: #1e293b; font-weight: 600;">${escapeHtml(time)}</p>
                    </td>
                  </tr>
                  <tr>
                    <td width="32" valign="top" style="padding-right: 12px;">
                      <div style="width: 32px; height: 32px; background: ${theme.primary}15; border-radius: 8px; text-align: center; line-height: 32px;">
                        <img src="https://img.icons8.com/ios-filled/24/${theme.primary.replace('#', '')}/marker.png" alt="" width="18" height="18" style="vertical-align: middle;" />
                      </div>
                    </td>
                    <td>
                      <p style="margin: 0; font-family: Arial, sans-serif; font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Venue</p>
                      <p style="margin: 4px 0 0 0; font-family: Arial, sans-serif; font-size: 15px; color: #1e293b; font-weight: 600;">${escapeHtml(venue)}</p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
          ${descriptionSection}
          ${youtubeSection}
        </td>
      </tr>
    </table>
  `;
}

/**
 * Generate hero section for featured event (legacy - now uses unified card)
 */
function generateHeroSection(event: HMCEvent): string {
  return generateUnifiedEventCard(event, { showYouTube: true, showDescription: true });
}

/**
 * Generate artist/event description section
 * - Concerts: "About the Artist"
 * - Sporting events: "Matchup" (only if matchup data exists)
 * - Other: "Event Details"
 */
function generateDescriptionSection(event: HMCEvent): string {
  const isSporting = isSportingEvent(event);
  const isConcert = event.music_genre && event.music_genre !== 'other';

  // For sporting events, never show "Event Details" - only show if we have actual matchup info
  if (isSporting) {
    // Skip description section entirely for sporting events
    // Matchup info would be displayed separately in VIPMatchupPanel in the UI
    return '';
  }

  const description = event.event_description || event.special_event;
  if (!description) return '';

  const theme = getTeamTheme(event);
  const label = isConcert ? 'About the Artist' : 'Event Details';

  // Uses solid white background with left border for Outlook VBA compatibility (no gradients)
  return `
    <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin: 16px 0; border-collapse: collapse;">
      <tr>
        <td bgcolor="#ffffff" style="background-color: #ffffff; padding: 20px 24px; border-left: 4px solid ${theme.primary}; mso-highlight: transparent;">
          <p style="margin: 0 0 8px; font-family: Arial, sans-serif; font-size: 11px; font-weight: 700; color: ${theme.primary}; text-transform: uppercase; letter-spacing: 1.5px;">
            ${label}
          </p>
          <p style="margin: 0; font-family: Arial, sans-serif; font-size: 15px; color: #374151; line-height: 1.7; mso-highlight: transparent;">
            ${escapeHtml(description)}
          </p>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Generate guest link CTA banner with View All Events button
 * Used at top and bottom of email for prominent placement
 * Note: USI logo is only shown in footer section, not in banners
 *
 * Uses bulletproof button technique that works in:
 * - Outlook Desktop (table-based with bgcolor)
 * - Outlook VBA HTMLBody (double-nested tables with explicit bgcolor)
 * - Gmail, Apple Mail, Yahoo (full CSS support)
 *
 * IMPORTANT: No MSO conditional comments here! Outlook VBA's HTMLBody
 * doesn't process <!--[if !mso]> properly, causing content to be hidden.
 * Double-nested tables with bgcolor work universally without conditionals.
 */
function generateUSIBanner(guestLinkUrl: string, position: 'top' | 'bottom'): string {
  // Bulletproof button using double-nested table approach
  // NO MSO conditionals - works universally in all email clients including Outlook VBA
  return `
    <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin: 0; border-collapse: collapse;">
      <tr>
        <td align="center" bgcolor="${USI_BRANDING.navy}" style="background-color: ${USI_BRANDING.navy}; padding: 28px 20px; mso-highlight: transparent;">
          <!-- Double-nested table for Outlook VBA bulletproof button -->
          <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin: 0 auto; border-collapse: collapse;">
            <tr>
              <td align="center" bgcolor="${USI_BRANDING.gold}" style="background-color: ${USI_BRANDING.gold}; border-radius: 6px;">
                <!-- Inner table ensures bgcolor is applied in Outlook VBA -->
                <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse: collapse;">
                  <tr>
                    <td bgcolor="${USI_BRANDING.gold}" style="background-color: ${USI_BRANDING.gold}; padding: 14px 36px; border-radius: 6px;">
                      <a href="${escapeHtml(guestLinkUrl)}" target="_blank"
                         style="font-family: Arial, Helvetica, sans-serif;
                                font-size: 14px; font-weight: bold; color: ${USI_BRANDING.navyDark};
                                text-decoration: none; text-transform: uppercase; letter-spacing: 1px;
                                line-height: 1.2; mso-highlight: transparent;">
                        VIEW ALL EVENTS &rarr;
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
          <p style="margin: 16px 0 0 0; font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #ffffff; mso-highlight: transparent;">
            Explore our complete event calendar
          </p>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Generate guest link CTA button (legacy - now uses bulletproof button technique)
 * Uses double-nested table-based buttons for maximum email client compatibility.
 * NO MSO conditionals - works universally including Outlook VBA HTMLBody.
 */
function generateGuestLinkButton(
  guestLinkUrl: string,
  theme: (typeof TEAM_GRADIENTS)['dynamo']
): string {
  return `
    <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin: 32px 0; border-collapse: collapse;">
      <tr>
        <td align="center">
          <!-- Double-nested table for Outlook VBA bulletproof button -->
          <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin: 0 auto; border-collapse: collapse;">
            <tr>
              <td align="center" bgcolor="${USI_BRANDING.navy}" style="border-radius: 8px; background-color: ${USI_BRANDING.navy};">
                <!-- Inner table ensures bgcolor is applied in Outlook VBA -->
                <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse: collapse;">
                  <tr>
                    <td bgcolor="${USI_BRANDING.navy}" style="background-color: ${USI_BRANDING.navy}; padding: 18px 56px; border-radius: 8px;">
                      <a href="${escapeHtml(guestLinkUrl)}" target="_blank"
                         style="font-family: Arial, Helvetica, sans-serif;
                                font-size: 16px; font-weight: bold; color: #ffffff;
                                text-decoration: none; letter-spacing: 0.5px;
                                line-height: 1.2; mso-highlight: transparent;">
                        VIEW ALL EVENTS &rarr;
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
      <tr>
        <td align="center" style="padding-top: 14px;">
          <p style="margin: 0; font-family: Arial, Helvetica, sans-serif; font-size: 13px; color: #9ca3af; mso-highlight: transparent;">
            Click to view our complete event calendar
          </p>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Generate premium event card with optional gradient border and VML fallback
 */
function generatePremiumEventCard(
  event: HMCEvent,
  config: { showYouTube?: boolean; showDescription?: boolean; showGradientBorder?: boolean }
): string {
  const imageUrl = getEventImageUrl(event);
  const eventName = getCleanEventName(event.opponent);
  const formattedDate = formatEventDate(event.event_date);
  const time = event.kickoff_time || event.event_start_time || 'TBD';
  const venue = getEventVenue(event);
  const theme = getTeamTheme(event);
  const videoId = event.youtube_url ? extractYouTubeVideoId(event.youtube_url) : null;
  const showBorder = config.showGradientBorder !== false; // Default to true for backwards compatibility
  const isSporting = isSportingEvent(event);

  // Image section with rounded corners
  let imageSection: string;
  if (imageUrl) {
    imageSection = `
      <tr>
        <td style="padding: 0;">
          <img
            src="${escapeHtml(imageUrl)}"
            alt="${escapeHtml(eventName)}"
            width="100%"
            style="display: block; width: 100%; height: auto; max-height: 200px; object-fit: cover;"
          />
        </td>
      </tr>
    `;
  } else {
    imageSection = `
      <tr>
        <td style="overflow: hidden;">
          ${generateSportsPlaceholder(event.event_type)}
        </td>
      </tr>
    `;
  }

  // Genre badge for concerts
  let genreBadge = '';
  if (event.music_genre && event.music_genre !== 'other') {
    const genreInfo = GENRE_COLORS[event.music_genre];
    genreBadge = `
      <p style="margin: 16px 0 0 0;">
        <span style="display: inline-block; background: linear-gradient(135deg, ${genreInfo.bg}, ${genreInfo.bg}dd); color: white; padding: 6px 16px; border-radius: 20px; font-family: Arial, sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 0.5px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
          ${genreInfo.label}
        </span>
      </p>
    `;
  }

  // YouTube thumbnail with play button - NEVER for sporting events
  let youtubeSection = '';
  if (config.showYouTube !== false && videoId && !isSporting) {
    youtubeSection = generateYouTubeThumbnail(videoId, eventName);
  }

  // Description section
  let descriptionSection = '';
  if (config.showDescription !== false) {
    descriptionSection = generateDescriptionSection(event);
  }

  // Premium card - FULL WIDTH on mobile, gradient border is optional
  // Add visual separator before card for clearer distinction
  return `
    <!-- Event Separator - Clear visual break between events -->
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="margin-bottom: 12px;">
      <tr>
        <td style="padding: 0 20px;">
          <div style="height: 2px; background: linear-gradient(90deg, transparent, ${theme.primary}60, transparent);"></div>
        </td>
      </tr>
    </table>
    <!-- Premium Event Card ${showBorder ? 'with Gradient Border' : ''} -->
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation" class="event-card" style="margin-bottom: 32px; width: 100%;">
      <tr>
        <td style="width: 100%;">
          <!--[if mso]>
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="${showBorder ? `border: 3px solid ${theme.primary};` : 'border: 1px solid #e5e7eb;'}">
            <tr>
              <td style="background-color: #ffffff; padding: 0;">
          <![endif]-->
          <!--[if !mso]><!-->
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="${showBorder ? `background: ${theme.gradient}; padding: 4px;` : 'border: 1px solid #e5e7eb;'} border-radius: 16px; width: 100%;">
            <tr>
              <td style="width: 100%;">
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; width: 100%;">
          <!--<![endif]-->
                    ${imageSection}
                    <tr>
                      <td style="padding: 24px;">
                        <!-- Event Name with Accent -->
                        <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                          <tr>
                            <td>
                              <div style="width: 48px; height: 4px; background: ${theme.gradient}; border-radius: 2px; margin-bottom: 16px;"></div>
                              <h2 style="margin: 0 0 16px 0; font-family: Arial, sans-serif; font-size: 22px; font-weight: bold; color: #1f2937; line-height: 1.3;">
                                ${escapeHtml(eventName)}
                              </h2>
                            </td>
                          </tr>
                        </table>

                        <!-- Event Details -->
                        <table width="100%" cellpadding="0" cellspacing="0" role="presentation" class="event-details" style="background: #f9fafb; border-radius: 8px; padding: 16px;">
                          <tr>
                            <td>
                              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                                <tr>
                                  <td width="28" valign="top" style="padding-right: 10px;">
                                    <img src="${ICON_URLS.calendarColored(theme.primary)}" alt="" width="18" height="18" style="display:block;" />
                                  </td>
                                  <td style="padding-bottom: 10px;">
                                    <p style="margin: 0; font-family: Arial, sans-serif; font-size: 15px; color: #374151; font-weight: 500;">
                                      ${escapeHtml(formattedDate)}
                                    </p>
                                  </td>
                                </tr>
                                <tr>
                                  <td width="28" valign="top" style="padding-right: 10px;">
                                    <img src="${ICON_URLS.clockColored(theme.primary)}" alt="" width="18" height="18" style="display:block;" />
                                  </td>
                                  <td style="padding-bottom: 10px;">
                                    <p style="margin: 0; font-family: Arial, sans-serif; font-size: 15px; color: #374151; font-weight: 500;">
                                      ${escapeHtml(time)}
                                    </p>
                                  </td>
                                </tr>
                                <tr>
                                  <td width="28" valign="top" style="padding-right: 10px;">
                                    <img src="${ICON_URLS.mapPinColored(theme.primary)}" alt="" width="18" height="18" style="display:block;" />
                                  </td>
                                  <td>
                                    <p style="margin: 0; font-family: Arial, sans-serif; font-size: 15px; color: #374151; font-weight: 500;">
                                      ${escapeHtml(venue)}
                                    </p>
                                  </td>
                                </tr>
                              </table>
                            </td>
                          </tr>
                        </table>

                        ${genreBadge}
                        ${descriptionSection}
                        ${youtubeSection}
                      </td>
                    </tr>
          <!--[if !mso]><!-->
                </table>
              </td>
            </tr>
          </table>
          <!--<![endif]-->
          <!--[if mso]>
              </td>
            </tr>
          </table>
          <![endif]-->
        </td>
      </tr>
    </table>
  `;
}

/**
 * Generate HTML for a single event card (legacy - uses premium by default)
 */
function generateEventCardHtml(event: HMCEvent): string {
  return generatePremiumEventCard(event, { showYouTube: true, showDescription: true });
}

/**
 * Category configuration for section headers
 */
const CATEGORY_CONFIG = {
  sporting: {
    title: 'Sporting Events',
    icon: 'https://img.icons8.com/fluency/48/trophy.png',
    gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    primaryColor: '#f97316',
    accentColor: '#fed7aa',
  },
  concerts: {
    title: 'Live Concerts',
    icon: 'https://img.icons8.com/fluency/48/musical-notes.png',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    primaryColor: '#8b5cf6',
    accentColor: '#ddd6fe',
  },
  other: {
    title: 'Special Events',
    icon: 'https://img.icons8.com/fluency/48/calendar.png',
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    primaryColor: '#0ea5e9',
    accentColor: '#bae6fd',
  },
};

/**
 * Generate a polished intro caption for the events section
 * Uses solid white background for Outlook VBA compatibility (no gradients)
 */
function generateEventsIntroCaption(totalEvents: number): string {
  return `
    <tr>
      <td style="padding: 24px 32px 16px; background-color: #ffffff;">
        <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse: collapse;">
          <tr>
            <td bgcolor="#ffffff" style="background-color: #ffffff; padding: 20px 24px; border: 1px solid #e2e8f0; border-radius: 12px; mso-highlight: transparent;">
              <p style="margin: 0; font-family: Arial, sans-serif; font-size: 17px; color: #334155; line-height: 1.6; text-align: center; mso-highlight: transparent;">
                <span style="font-weight: 600; color: #1e293b;">Discover ${totalEvents} handpicked event${totalEvents !== 1 ? 's' : ''}</span>
                <br/>
                <span style="font-size: 15px; color: #64748b;">Explore our curated selection across sports, concerts, and exclusive experiences.</span>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  `;
}

/**
 * Generate a section header for event categories
 */
function generateSectionHeader(category: 'sporting' | 'concerts' | 'other', eventCount: number): string {
  const config = CATEGORY_CONFIG[category];

  return `
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="margin: 20px 0 16px 0;">
      <tr>
        <td style="padding: 16px 0; border-bottom: 2px solid ${config.primaryColor}20;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td width="44" valign="middle">
                <div style="width: 36px; height: 36px; background: ${config.primaryColor}15; border-radius: 10px; text-align: center; line-height: 36px;">
                  <img src="${config.icon}" alt="" width="22" height="22" style="vertical-align: middle;" />
                </div>
              </td>
              <td valign="middle" style="padding-left: 12px;">
                <h3 style="margin: 0; font-family: Arial, sans-serif; font-size: 17px; font-weight: 700; color: #1f2937;">
                  ${config.title}
                </h3>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Generate categorized events HTML with section headers
 * Uses compact mode when many events to prevent Gmail clipping (102KB limit)
 */
function generateCategorizedEventsHtml(
  eventCategories: { sporting: HMCEvent[]; concerts: HMCEvent[]; other: HMCEvent[] },
  options: { showYouTube: boolean; showDescription: boolean; compact?: boolean }
): string {
  const sections: string[] = [];
  const cardOptions = { ...options, compact: options.compact };

  // Sporting Events
  if (eventCategories.sporting.length > 0) {
    const header = options.compact ? '' : generateSectionHeader('sporting', eventCategories.sporting.length);
    const cards = eventCategories.sporting
      .map(event => generateUnifiedEventCard(event, cardOptions))
      .join('\n');
    sections.push(header + cards);
  }

  // Concerts
  if (eventCategories.concerts.length > 0) {
    const header = options.compact ? '' : generateSectionHeader('concerts', eventCategories.concerts.length);
    const cards = eventCategories.concerts
      .map(event => generateUnifiedEventCard(event, cardOptions))
      .join('\n');
    sections.push(header + cards);
  }

  // Other/Special Events
  if (eventCategories.other.length > 0) {
    const header = options.compact ? '' : generateSectionHeader('other', eventCategories.other.length);
    const cards = eventCategories.other
      .map(event => generateUnifiedEventCard(event, cardOptions))
      .join('\n');
    sections.push(header + cards);
  }

  return sections.join('\n');
}

/**
 * Generate the complete email template HTML - Premium Edition
 */
export function generateEmailTemplate(config: EmailTemplateConfig): string {
  const {
    events,
    personalMessage,
    senderName,
    includeFooter,
    includeLogo,
    includeGuestLink = false,
    guestLinkUrl = '',
    stylePreset = 'premium',
    showArtistDescriptions = true,
    showYouTubeThumbnails = true,
    eventCategories,
    signatureHtml = '',
  } = config;

  // Determine the primary theme from the first event
  const primaryTheme = events.length > 0 ? getTeamTheme(events[0]) : TEAM_GRADIENTS.general;

  // Check if we have categorized events (6-slot mode)
  const hasCategorizedEvents = eventCategories && (
    eventCategories.sporting.length > 0 ||
    eventCategories.concerts.length > 0 ||
    eventCategories.other.length > 0
  );

  // Auto-enable compact mode for 3+ events to prevent Gmail clipping (102KB limit)
  // Compact mode removes VML fallbacks, reduces HTML comments, and simplifies styling
  const useCompactMode = events.length >= 3;

  // Generate hero section for first event (if premium style and NOT using categories and not compact)
  const heroSection =
    stylePreset === 'premium' && events.length > 0 && !hasCategorizedEvents && !useCompactMode
      ? generateHeroSection(events[0])
      : '';

  // Generate event cards - either categorized or flat list
  let eventCardsHtml: string;
  if (hasCategorizedEvents && eventCategories) {
    // Use categorized rendering with section headers
    // Compact mode automatically used for 3+ events to prevent Gmail clipping
    eventCardsHtml = generateCategorizedEventsHtml(eventCategories, {
      showYouTube: showYouTubeThumbnails && !useCompactMode, // Skip YouTube embeds in compact mode (too much HTML)
      showDescription: showArtistDescriptions && !useCompactMode,
      compact: useCompactMode,
    });
  } else {
    // Legacy flat list rendering
    const remainingEvents = stylePreset === 'premium' && events.length > 1 && !useCompactMode ? events.slice(1) : events;
    eventCardsHtml = remainingEvents
      .map((event) =>
        generateUnifiedEventCard(event, {
          showYouTube: showYouTubeThumbnails && !useCompactMode,
          showDescription: showArtistDescriptions && !useCompactMode,
          compact: useCompactMode,
        })
      )
      .join('\n');
  }

  // Generate intro caption for categorized events
  const introCaption = hasCategorizedEvents ? generateEventsIntroCaption(events.length) : '';

  // Guest link CTA button
  const guestLinkSection =
    includeGuestLink && guestLinkUrl
      ? generateGuestLinkButton(guestLinkUrl, primaryTheme)
      : '';

  // Personal message section with premium styling - full width with USI navy accent
  // Note: personalMessage may contain HTML formatting from Outlook/Word, so we don't escape it
  // Uses solid white background with left border only for Outlook VBA compatibility (no gradients)
  const personalMessageSection = personalMessage.trim()
    ? `
      <tr>
        <td style="padding: 32px 40px; background-color: #ffffff;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse: collapse;">
            <tr>
              <td bgcolor="#ffffff" style="background-color: #ffffff; border-left: 4px solid ${USI_BRANDING.navy}; padding: 28px; mso-highlight: transparent;">
                <!-- Personal Message - HTML formatting preserved from Outlook/Word paste -->
                <div style="margin: 0; font-family: Arial, sans-serif; font-size: 16px; color: #374151; line-height: 1.7; mso-highlight: transparent;">
                  ${personalMessage.trim()}
                </div>
                ${senderName.trim() ? `<p style="margin: 20px 0 0 0; font-family: Arial, sans-serif; font-size: 15px; color: #6b7280; font-weight: 500;">— ${escapeHtml(senderName.trim())}</p>` : ''}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    `
    : '';

  // Logo section removed - USI logo now only appears in the footer section
  // The includeLogo option still controls whether the logo shows in the footer
  const logoSection = ''; // Logo moved to footer only per user request

  // USI Banner at top (if guest link enabled)
  const topBannerSection =
    includeGuestLink && guestLinkUrl
      ? generateUSIBanner(guestLinkUrl, 'top')
      : '';

  // USI Banner at bottom (if guest link enabled)
  const bottomBannerSection =
    includeGuestLink && guestLinkUrl
      ? generateUSIBanner(guestLinkUrl, 'bottom')
      : '';

  // Email signature section (preserves all Outlook formatting including fonts, colors, images)
  // The signature HTML is inserted directly without escaping to preserve original formatting
  const signatureSection = signatureHtml.trim()
    ? `
      <tr>
        <td style="padding: 24px 32px 32px; background-color: white;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="border-top: 1px solid #e5e7eb; padding-top: 24px;">
            <tr>
              <td>
                <!-- User's Email Signature (preserves all original formatting) -->
                <div class="email-signature" style="font-family: inherit;">
                  ${signatureHtml}
                </div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    `
    : '';

  // Footer section with USI corporate styling - logo only, no text
  const footerSection = includeFooter
    ? `
      <tr>
        <td>
          <!-- USI Navy accent bar -->
          <div style="height: 4px; background: linear-gradient(90deg, ${USI_BRANDING.gold}, ${USI_BRANDING.goldLight}, ${USI_BRANDING.gold});"></div>
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background-color: ${USI_BRANDING.navyDark};">
            <tr>
              <td style="padding: 32px 40px; text-align: center;">
                <img
                  src="${USI_BRANDING.logoWhite}"
                  alt="USI Insurance Services"
                  width="180"
                  style="display: block; margin: 0 auto; max-width: 180px; height: auto;"
                />
              </td>
            </tr>
          </table>
        </td>
      </tr>
    `
    : '';

  // Responsive styles (work in Gmail, Apple Mail - ignored by Outlook)
  // FIXED: Mobile event cards now use full width with proper padding
  const responsiveStyles = `
    <style type="text/css">
      @media only screen and (max-width: 700px) {
        .email-container { width: 100% !important; padding: 8px !important; }
        .email-container > tbody > tr > td { padding-left: 12px !important; padding-right: 12px !important; }
        .content-padding { padding: 16px !important; }
        .hero-title { font-size: 22px !important; }
        .event-card { margin-bottom: 16px !important; width: 100% !important; }
        .event-card table { width: 100% !important; }
        .event-card img { width: 100% !important; max-width: 100% !important; }
        .cta-button { width: 100% !important; display: block !important; text-align: center !important; padding: 16px 24px !important; }
        .usi-banner { padding: 24px 16px !important; }
        .usi-banner img { width: 140px !important; max-width: 140px !important; }
        .usi-banner a { padding: 12px 28px !important; font-size: 13px !important; }
        .event-details { padding: 12px !important; }
        .event-details td { padding-bottom: 8px !important; }
        /* Full-width event images on mobile */
        td[style*="padding: 24px"] { padding: 16px !important; }
        td[style*="padding: 32px"] { padding: 16px !important; }
        td[style*="padding: 40px"] { padding: 16px !important; }
      }
    </style>
  `;

  return `<!DOCTYPE html>
<html lang="en" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="utf-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self' data: blob: https:; style-src 'self' 'unsafe-inline'; img-src 'self' data: https: blob:; font-src 'self' data:;">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="x-apple-disable-message-reformatting">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="color-scheme" content="light">
  <meta name="supported-color-schemes" content="light">
  <title>USI Suite Events</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <style>
    table { border-collapse: collapse; }
    td { font-family: Arial, sans-serif; }
  </style>
  <![endif]-->
  <!--[if !mso]><!-->
  ${responsiveStyles}
  <!--<![endif]-->
</head>
<body style="margin: 0; padding: 0; background-color: #f3f4f6; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">
  <!-- Wrapper table -->
  <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #f3f4f6;">
    <tr>
      <td align="center" style="padding: 40px 16px;">

        <!-- Content container - wider for desktop -->
        <table width="700" cellpadding="0" cellspacing="0" role="presentation" class="email-container" style="max-width: 700px; width: 100%; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,0.08);">

          <!-- Personal Message - FIRST, before USI header -->
          ${personalMessageSection}

          <!-- Premium Header (USI Hero Image) - After personal message -->
          <tr>
            <td>
              <!--[if mso]>
              <v:rect xmlns:v="urn:schemas-microsoft-com:vml" style="width:700px;height:120px;" fillcolor="${USI_BRANDING.navy}">
                <v:textbox style="mso-fit-shape-to-text:false;" inset="30px,20px,30px,0">
                  <center>
                    <img src="${USI_BRANDING.logoWhite}" alt="USI" width="160" style="margin-bottom:10px;" />
                    <br/>
                    <span style="font-family:Arial,sans-serif;font-size:13px;color:#ffffff;">
                      Exclusive Suite Invitation to Toyota Center
                    </span>
                  </center>
                </v:textbox>
              </v:rect>
              <![endif]-->
              <!--[if !mso]><!-->
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background: linear-gradient(135deg, ${USI_BRANDING.navyDark} 0%, ${USI_BRANDING.navy} 50%, ${USI_BRANDING.navyLight} 100%);">
                <tr>
                  <td style="padding: 32px 40px; text-align: center;">
                    <img
                      src="${USI_BRANDING.logoWhite}"
                      alt="USI Insurance Services"
                      width="180"
                      style="display: block; margin: 0 auto 14px; max-width: 180px; height: auto;"
                    />
                    <p style="margin: 0; font-family: Arial, sans-serif; font-size: 15px; color: rgba(255,255,255,0.9); font-weight: 500; letter-spacing: 0.5px;">
                      Exclusive Suite Invitation to Toyota Center
                    </p>
                  </td>
                </tr>
              </table>
              <!--<![endif]-->
            </td>
          </tr>

          <!-- TOP USI BANNER with View All Events (if guest link enabled) -->
          ${topBannerSection ? `
          <tr>
            <td class="usi-banner">
              ${topBannerSection}
            </td>
          </tr>
          ` : ''}

          ${logoSection}

          <!-- Intro Caption (for categorized events) -->
          ${introCaption}

          <!-- Hero Section (First Event) -->
          ${
            heroSection
              ? `
          <tr>
            <td style="padding: 24px 32px 8px; background-color: white;">
              ${heroSection}
            </td>
          </tr>
          `
              : ''
          }

          <!-- Additional Events Section -->
          ${
            eventCardsHtml
              ? `
          <tr>
            <td style="padding: ${heroSection ? '16px' : personalMessage.trim() || hasCategorizedEvents ? '8px' : '32px'} 32px 8px; background-color: white;">
              ${
                // Skip section header if using categorized events (they have their own headers)
                hasCategorizedEvents
                  ? ''
                  : !heroSection
                    ? `
              <h2 style="margin: 0 0 20px; font-family: Arial, sans-serif; font-size: 20px; font-weight: bold; color: #1f2937;">
                <span style="display: inline-block; width: 4px; height: 24px; background: ${primaryTheme.gradient}; border-radius: 2px; margin-right: 12px; vertical-align: middle;"></span>
                ${events.length === 1 ? 'Featured Event' : 'Featured Events'}
              </h2>
              `
                    : events.length > 2
                      ? `
              <h2 style="margin: 0 0 20px; font-family: Arial, sans-serif; font-size: 18px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 1px;">
                More Events
              </h2>
              `
                      : ''
              }
              ${eventCardsHtml}
            </td>
          </tr>
          `
              : ''
          }

          <!-- Guest Link CTA (middle section - only if no banner) -->
          ${
            guestLinkSection && !topBannerSection
              ? `
          <tr>
            <td style="padding: 8px 32px 32px; background-color: white;">
              ${guestLinkSection}
            </td>
          </tr>
          `
              : ''
          }

          <!-- BOTTOM USI BANNER with View All Events (if guest link enabled) -->
          ${bottomBannerSection ? `
          <tr>
            <td class="usi-banner">
              ${bottomBannerSection}
            </td>
          </tr>
          ` : ''}

          <!-- Email Signature (if provided) -->
          ${signatureSection}

          ${footerSection}

        </table>

      </td>
    </tr>
  </table>
</body>
</html>`;
}

/**
 * Download the email template as an HTML file
 */
export function downloadEmailTemplate(html: string, filename?: string): void {
  const defaultFilename = `USI-Events-Template-${new Date().toISOString().split('T')[0]}.html`;
  const finalFilename = filename || defaultFilename;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = finalFilename;
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
